import { Col, Container, Row, Badge, Tabs, Tab } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { useParamIds } from "./Data/Common";
import { showDuration } from "./Data/Date";
import { showMoneyShort } from "./Data/Money";
import { Facility, RateProgram, ValidationType } from "./Data/ApiTypes";
import { rateProgramType, showApplicableDays } from "./Data/RateProgram";
import { PencilFill, QrCode, InfoCircleFill } from "react-bootstrap-icons";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { Api } from "./Data/Api";
import { useState, useEffect } from "react";

export function FacilityRateListPage() {
    const nav = useNavigate();
    const { facilityId } = useParamIds();
    const ratePrograms = useLoaderData() as RateProgram[];
    const [searchParams] = useSearchParams();
    const [programs, setPrograms] = useState(ratePrograms);
    const [showInactiveTraditionalRates, setShowInactiveTraditionalRates] = useState(false);
    const [showInactiveEventRates, setShowInactiveEventRates] = useState(false);
    const [showInactiveValidationRates, setShowInactiveValidationRates] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    
    // Initialize active tab from URL parameter or default to "hourly"
    const getInitialTab = (): "hourly" | "event" | "validation" => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'event' || tabParam === 'validation' || tabParam === 'hourly') {
            return tabParam;
        }
        return "hourly";
    };
    
    const [activeTab, setActiveTab] = useState<"hourly" | "event" | "validation">(getInitialTab());

    // Update active tab when URL parameter changes
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'event' || tabParam === 'validation' || tabParam === 'hourly') {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    const handleToggleActive = async (rateProgramId: number, isActive: boolean) => {
        await Api.facilityRateStatusUpdate(facilityId, rateProgramId, isActive).then(res => {
            setPrograms(prev =>
                prev.map(p =>
                    p.rateProgramId === rateProgramId ? { ...p, isActive } : p
                )
            );
        }).catch(err => {
            console.error("Failed to change rate status:", err);
        });
    }

    return <Container style={{ overflowY: "hidden"}}>
        
        <Row className="justify-content-left mb-3">
            <Col xs="auto">
                <Button className="w-100 btn-main" onClick={() => nav( `/facility/${facilityId}/rate/create?tab=${activeTab}` )}>
                    Create New Rate
                </Button>
            </Col>
        </Row>
        <Row className="justify-content-left">
            <Col xl="6" xxl="5" className="mb-2">
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as typeof activeTab)}>
            <Tab eventKey="hourly" title="Hourly Rates">
                {programs?.filter(r => !r.isEvent).length > 0 && <>
                  
                    <ToolButton className="my-3" onClick={() => nav( `/facility/${facilityId}/rate/sign` )}>
                        <QrCode color="maroon" /> View QR Code
                    </ToolButton>
                 
                        <div className="form-check form-switch mb-3 mt-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="showInactiveToggle"
                                checked={showInactiveTraditionalRates}
                                onChange={() => setShowInactiveTraditionalRates(!showInactiveTraditionalRates)}
                            />
                            <label className="form-check-label" htmlFor="showInactiveToggle">
                                Show Inactive Rates
                            </label>
                        </div>
                        {programs?.filter(r => !r.isEvent && !r.isValidation)
                        .filter(r => showInactiveTraditionalRates || r.isActive)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(prog =>
                        <div key={prog.rateProgramId}>
                            <div className="d-flex-align-center gap-2 mb-2">
                                {prog.start && new Date(prog.start.toString()) > new Date() && (
                                    <Badge >Future</Badge>
                                )}
                                {(!prog.isActive) && (
                                    <Badge bg="secondary">Inactive</Badge>
                                )}
                                {(prog.end && new Date(prog.end.toString()) < new Date()) && (
                                    <Badge bg="secondary">Ended</Badge>
                                )}
                                <span className="fs-5 fw-bold">
                                    {prog.name}
                                </span>
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`toggle-${prog.rateProgramId}`}
                                        checked={prog.isActive}
                                        onChange={(e) => handleToggleActive(prog.rateProgramId, e.target.checked)}
                                    />
                                    <label className="form-check-label" style={{fontSize:".9rem"}} htmlFor={`toggle-${prog.rateProgramId}`}>
                                        Active
                                    </label>
                                </div>
                                <Button variant="sm"
                                    className="d-flex align-items-center ms-auto"
                                    style={{ padding: "1px 0.5em 1px 0.5em" }}
                                    onClick={() => nav( `/facility/${facilityId}/rate/${prog.rateProgramId}` )}>
                                    <PencilFill color="maroon" />&nbsp; Edit Rate
                                </Button>
                            </div>
                            <RateProgramTable prog={prog} />
                        </div> )}
                </>}
                </Tab>
                <Tab eventKey="event" title="Event Rates">
                   
                    <div className="form-check form-switch mb-3 mt-3">
                        <input
                        className="form-check-input"
                        type="checkbox"
                        id="showInactiveToggleValidation"
                        checked={showInactiveEventRates}
                        onChange={() => setShowInactiveEventRates(!showInactiveEventRates)}
                        />
                        <label className="form-check-label" htmlFor="showInactiveToggleValidation">
                        Show Inactive Rates
                        </label>
                    </div>
                    <div className="alert mb-3 d-flex align-items-center gap-2 alert-light-bg">
                        <InfoCircleFill />
                        Events can only be accessed via a direct link.
                    </div>
                    {programs?.filter(r => r.isEvent).length > 0 && <>
                    {programs?.filter(r => r.isEvent)
                    .filter(r => showInactiveEventRates || r.isActive)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(prog =>
                    <div key={prog.rateProgramId}>
                         <span className="fs-5 fw-bold">
                                {prog.name}
                            </span>      
                        <div className="d-flex align-items-center gap-2 mb-2">                            
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`toggle-${prog.rateProgramId}`}
                                    checked={prog.isActive}
                                    onChange={(e) => handleToggleActive(prog.rateProgramId, e.target.checked)}
                                />
                                <label className="form-check-label" style={{fontSize:".9rem"}} htmlFor={`toggle-${prog.rateProgramId}`}>
                                    Active
                                </label>
                            </div>
                            <div className="ms-auto d-flex gap-2">
                                {prog.isActive &&  
                                <Button variant="sm"
                                    className="d-flex align-items-center"              
                                    style={{ padding: "1px 0.5em 1px 0.5em" }}
                                    onClick={() => nav( `/facility/${facilityId}/rate/sign/${prog.rateProgramId}` )}>
                                    <QrCode color="maroon" /> &nbsp; View QR Code
                                </Button>   
                                }
                                <Button variant="sm"
                                    className="d-flex align-items-center"
                                    style={{ padding: "1px 0.5em 1px 0.5em" }}
                                    onClick={() => nav( `/facility/${facilityId}/rate/${prog.rateProgramId}` )}>
                                    <PencilFill color="maroon" /> &nbsp; Edit Rate
                                </Button>             
                            </div>
                        </div>
                        <RateProgramTable prog={prog} />
                    </div> )}
                </>}
                </Tab>
                <Tab eventKey="validation" title="Validation Rates">
              
                    <div className="form-check form-switch mb-3 mt-3">
                        <input
                        className="form-check-input"
                        type="checkbox"
                        id="showInactiveToggleValidation"
                        checked={showInactiveValidationRates}
                        onChange={() => setShowInactiveValidationRates(!showInactiveValidationRates)}
                        />
                        <label className="form-check-label" htmlFor="showInactiveToggleValidation">
                        Show Inactive Rates
                        </label>
                    </div>
                    <div className="alert mb-3 d-flex align-items-start gap-2 alert-light-bg">
                    <InfoCircleFill className="mt-1" />

                    <div className="flex-fill">

                        <div
                        className="d-flex justify-content-between align-items-center mb-1"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowHelp(!showHelp)}
                        >
                        <strong>About Validation Rates</strong>
                        <span className="text-primary">
                            {showHelp ? "Hide Details ▲" : "Show Details ▼"}
                        </span>
                        </div>

                        {showHelp && (
                        <div className="mt-2">

                            <p className="mb-2">
                            You can distribute these rates to your customers via a direct link or QR code.
                            </p>

                            <div className="mb-2">
                            
                            <ul className="mb-0 ms-0">

                                <li className="mb-2">
                                <strong>Discount Validation</strong>
                                <br />
                                <small className="text-muted">
                                    Shareable link or QR code that sends parkers to a page where their
                                    parking rate can be discounted by the configured amount.
                                </small>
                                </li>

                                <li>
                                <strong>Fixed Validation</strong>
                                <br />
                                <small className="text-muted">
                                    Shareable link that takes parkers to a request form. Their request
                                    will appear in your Transient → Requests tab for approval.
                                </small>
                                </li>

                            </ul>
                            </div>
                        </div>
                        )}

                    </div>
                    </div>

                    {programs?.filter(r => r.isValidation).length > 0 && 
                     <>
                    {programs
                    .filter(r => r.isValidation)
                    .filter(r => showInactiveValidationRates || r.isActive)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(prog => {
                        const isDiscount = prog.validationType === ValidationType.Discount;
                        const selectedId = isDiscount ? prog.encodedRateProgramId : prog.rateProgramId;
                        const qrUrl = `/facility/${facilityId}/rate/sign/${selectedId}/true/${isDiscount}`;
                        const editUrl = `/facility/${facilityId}/rate/${prog.rateProgramId}`;

                        return (
                        <div key={prog.rateProgramId}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                            <Badge
                                className="bg-opacity-75 d-flex align-items-center"
                                style={{ fontSize: ".75rem" }}
                            >
                                {isDiscount ? "Discount" : "Fixed"}
                            </Badge>

                            <span className="fs-5 fw-bold">{prog.name}</span>
                            </div>

                            <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="form-check form-switch">
                                <input
                                className="form-check-input"
                                type="checkbox"
                                id={`toggle-${prog.rateProgramId}`}
                                checked={prog.isActive}
                                onChange={(e) => handleToggleActive(prog.rateProgramId, e.target.checked)}
                                />
                                <label
                                className="form-check-label"
                                style={{ fontSize: ".9rem" }}
                                htmlFor={`toggle-${prog.rateProgramId}`}
                                >
                                Active
                                </label>
                            </div>

                            <div className="ms-auto d-flex gap-2">
                                {prog.isActive && (
                                <Button
                                    variant="sm"
                                    className="d-flex align-items-center"
                                    style={{ padding: "1px 0.5em" }}
                                    onClick={() => nav(qrUrl)}
                                >
                                    <QrCode color="maroon" /> &nbsp; View QR Code
                                </Button>
                                )}

                                <Button
                                variant="sm"
                                className="d-flex align-items-center"
                                style={{ padding: "1px 0.5em" }}
                                onClick={() => nav(editUrl)}
                                >
                                <PencilFill color="maroon" /> &nbsp; Edit Rate
                                </Button>
                            </div>
                            </div>

                            <RateProgramTable prog={prog} />
                        </div>
                        );
                    })}
                    </>}
                </Tab>
                {(!programs || programs.length === 0) && 
                    <div className="alert alert-light-bg mb-3 d-flex align-items-center gap-2">
                       
                        No rate programs exist yet. Create your first rate program by clicking Create.
                    </div>
                }
            </Tabs>
            </Col>
        </Row>
    </Container>;
}

interface RateProgramTableProps {
    prog: RateProgram;
}

export function RateProgramTable( { prog }: RateProgramTableProps ) {
    const applicableDaysText = showApplicableDays(prog.applicableDays);
    const showDaysRow = applicableDaysText !== "Any day";
    
    return <div className="rounded-4 mb-3 overflow-hidden border"
            style={{ display:             "grid",
                     gridTemplateColumns: "1fr min-content 1fr 1fr" }}>
        <div className="d-grid fw-semibold bg-light border-bottom"
                style={{ gridColumn:          "1 / -1",
                        gridTemplateColumns: "subgrid" }}>
            <div className="p-2 text-center border-right"
                    style={{ gridColumn: "1 / 4" }}>
                {rateProgramType( prog )}
            </div>
            <div className="p-2 text-center">
                {prog.validationType == ValidationType.Discount ? 'Discount' :  'Price' }
            </div>
        </div>
        {showDaysRow && (
            <div className="bg-light text-muted" 
                 style={{ 
                     gridColumn: "1 / -1", 
                     padding: "0.25rem 0.5rem", 
                     fontSize: "0.875rem",
                     borderBottom: "1px solid #dee2e6"
                 }}>
                <strong>Days:</strong> {applicableDaysText}
            </div>
        )}

        {/* rows */}
        {prog?.entries.map( ( rate, i ) => {
            const [upTo, unit] = showDuration( rate.upTo ).split( " " );
            return <div key={i} style={{
                display: "grid",
                gridColumn: "1 / -1",
                gridTemplateColumns: "subgrid",
                borderBottom: i < prog.entries.length - 1 ? "1px solid #dee2e6" : "none"
            }}>
                {prog.isEvent || prog.isValidation ? (
                    <div style={{
                        padding: "0.5rem",
                        textAlign: "center",
                        gridColumn: "1 / 4",
                        borderRight: "1px solid #dee2e6"
                    }}>
                        Flat Rate
                    </div>
                ) : (
                    <>
                        <div style={{
                            padding: "0.5rem",
                            textAlign: "end",
                        }}>
                            Up to
                        </div>
                        <div style={{
                            padding: "0.5rem",
                            textAlign: "right",
                        }}>
                            {upTo}
                        </div>
                        <div style={{
                            padding: "0.5rem",
                            textAlign: "left",
                            borderRight: "1px solid #dee2e6"
                        }}>
                            {unit}
                        </div>
                    </>
                )}
                <div style={{
                    padding: "0.5rem",
                    textAlign: "center"
                }}>
                    {showMoneyShort( rate.rate )}
                </div>
            </div>
        } )}
    </div>;
}